import { computed, inject, Injectable, PLATFORM_ID, resource, signal, untracked } from "@angular/core";
import { environment } from "@environments/environment.development";
import { HttpClient } from "@angular/common/http";
import { ILoginResult, IShop, IUserInfo } from "./sys-login.defs";
import { isPlatformBrowser } from "@angular/common";
import { lastValueFrom, map, of, switchMap, tap } from "rxjs";
import { SYS_FIELDS_LOGIN, SYS_FIELDS_RECOVER_PASSWORD } from "./sys-login.fields";
import { Utils } from "@common/utils";
import { DateTime } from "luxon";
import { IDataFetch } from "@common/defs/data-fetch";

@Injectable({
    providedIn: 'root'
})
export class SysLoginService {
    private _shop = signal<IShop | undefined>(sessionStorage.getItem('shop') ? JSON.parse(sessionStorage.getItem('shop') || '') : undefined)
    private _token = signal<string>( sessionStorage.getItem('token') || '' )
    private _tokenExpirationTime = signal<number>(sessionStorage.getItem('token_expiration') ? Number(sessionStorage.getItem('token_expiration')) : 0)

    private httpClient = inject(HttpClient);    
    
    private readonly platformId = inject(PLATFORM_ID);        
    readonly shopSig = this._shop.asReadonly();

    private readonly _userInfo = resource({
        params: () => ({token: this._token()}),
        loader: ({params}) => this.fetchUserInfo(params.token)
    });

    private readonly _userName = computed(() => {
        this.ensureTokenLoaded(); 
        const info = this._userInfo.value() as IUserInfo | undefined;
        return info?.current_name || '';
    });
    
    private readonly _tokenValid = computed(() => this._token() !== '' && this._tokenExpirationTime() > DateTime.now().toMillis() )

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            const t = sessionStorage.getItem('token') || '';
            if (t && this._token() !== t) this._token.set(t);

            const exp = sessionStorage.getItem('token_expiration');
            if (exp) this._tokenExpirationTime.set(Number(exp));

            const s = sessionStorage.getItem('shop');
            if (s) this._shop.set(JSON.parse(s));
        }
    }

    private ensureTokenLoaded() {
        if ( this._token() !== '' ) return;
        untracked(() => {
            if ( isPlatformBrowser(this.platformId) ) {
                this._token.set( sessionStorage.getItem('token') || '' );                
            } else {
                this._token.set('')
            }
        });
    }

    private ensureShopLoaded() {
        if ( this._shop() ) return;

        untracked(() => {
            if ( isPlatformBrowser(this.platformId) ) {
                const s = sessionStorage.getItem('shop');
                this._shop.set(s ? JSON.parse(s) : undefined);
            } else {
                this._shop.set(undefined)
            }
        });
    }

    get token() {
        this.ensureTokenLoaded();
        return this._token()
    }

    get header() {
        this.ensureShopLoaded();

        const shop_ulid = this._shop()?.shop_ulid;

        return {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${this.token}`,
                ...(shop_ulid ? { "x-shop": shop_ulid } : {})
            }
        }
    }

    headerForShop(shop_ulid?: string) {

        this.ensureShopLoaded();

        const ulid = shop_ulid ?? this._shop()?.shop_ulid;

        return {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${this.token}`,
                ...(ulid ? { "x-shop": ulid } : {})
            }
        }
    }

    headerMultipartForShop(shop_ulid?: string, license_ulid?: string) {

        this.ensureShopLoaded();

        const shopUlid = shop_ulid ?? this._shop()?.shop_ulid;
        const licenseUlid = license_ulid ?? this._shop()?.license_ulid;

        return {
            headers: {
                Authorization: `Bearer ${this.token}`,
                ...(shopUlid ? { "x-shop": shopUlid } : {}),
                ...(licenseUlid ? { "x-license": licenseUlid } : {})
            }
        }
    }

    updateShop(payload: { shop_ulid: string; commercial_name: string; commercial_photo?: string; params?: string }) {

        const encodedName = encodeURIComponent(payload.commercial_name);

        return this.getShop(payload.shop_ulid).pipe(
            switchMap((shopFull: any) => {
                const shop = shopFull?.content?.[0] ?? shopFull;

                const body = { ...shop, commercial_name: payload.commercial_name,
                    license_ulid: shop?.license_ulid,
                    shop_ulid: shop?.shop_ulid ?? payload.shop_ulid,
                    commercial_photo: payload.commercial_photo,
                    params: payload.params !== undefined ? payload.params : shop?.params
                };

                return this.httpClient.put<any>(
                    `${environment.apiURL}/shop/name/${encodedName}`,
                    body,
                    this.headerForShop(shop?.shop_ulid ?? payload.shop_ulid)
                );
            }),
            tap((resp: any) => {
                const updatedShop = resp?.content?.[0] ?? resp;

                this._shop.set(updatedShop);
                sessionStorage.setItem('shop', JSON.stringify(updatedShop));
            })
        );
    }

    updateShopMultipart(formData: FormData) {

        this.ensureShopLoaded();

        const shop = this._shop();
        const shop_ulid = shop?.shop_ulid;

        if (!shop_ulid) throw new Error("shop_ulid não carregado no sessionStorage");

        const license_ulid = shop?.license_ulid ?? '';
        if (license_ulid) formData.set('license_ulid', license_ulid);

        const commercialName = String(formData.get('commercial_name') ?? '');
        const encodedName = encodeURIComponent(commercialName);

        return this.httpClient.put<any>(
            `${environment.apiURL}/shop/name/${encodedName}`,
            formData,
            this.headerMultipartForShop(shop_ulid, license_ulid)
        ).pipe(
            tap((resp: any) => {
                const updatedShop = resp?.content?.[0] ?? resp;

                this._shop.set(updatedShop);
                sessionStorage.setItem('shop', JSON.stringify(updatedShop));
            })
        );
    }

    private simpleAuthLoginHeader(username: string, password: string) {
        return {
            headers: {
              "Content-Type": 'application/json',
              Authorization: `Basic ${ btoa(`${username}:${password}`)}`
            }
        }
    }

    getShop(shop_ulid?: string) {
        return this.httpClient.get<any>(
            `${environment.apiURL}/shop`,
            this.headerForShop(shop_ulid)
        ).pipe(
            tap((resp: any) => {
                const fullShopData = resp?.content?.[0] ?? resp;
                if (fullShopData && (fullShopData.shop_ulid || fullShopData.legal_name)) {
                    this._shop.set(fullShopData);
                    sessionStorage.setItem('shop', JSON.stringify(fullShopData));
                }
            })
        );
    }

    get url() {
        return environment.apiURL;
    }

    get fieldsLogin() {
        return Array.from(SYS_FIELDS_LOGIN.values())
    }

    get fieldsRecoverPassword() {
        return Array.from(SYS_FIELDS_RECOVER_PASSWORD.values())
    }

    get userName() {
        return this._userName()
    }

    get shop() {
        return this._shop()
    }

    get tokenValid() {
        return this._tokenValid()
    }

    get userInfo() {
        return this._userInfo.value();
    }

    login(username: string, password: string) {
        return this.httpClient.get<ILoginResult>(`${this.url}/login`, 
            this.simpleAuthLoginHeader(username, password))
        .pipe(switchMap((response: ILoginResult) => {
                this._token.set(response.token_ulid);
                this._tokenExpirationTime.set( Utils.StrDateTimeToUnix(response.expiration) );
                sessionStorage.setItem('token', response.token_ulid);
                sessionStorage.setItem('token_expiration', Utils.StrDateTimeToUnix(response.expiration));

                if ( response.shops.length == 1 ) {
                    const baseShop = response.shops[0];
                    sessionStorage.setItem('shop', JSON.stringify(baseShop));
                    this._shop.set(baseShop);

                    return this.getShop(baseShop.shop_ulid).pipe(
                        tap((shopFull: any) => {
                            const full = shopFull?.content?.[0] ?? shopFull;
                            this._shop.set(full);
                            sessionStorage.setItem('shop', JSON.stringify(full));
                        }), map(() => response.shops));
                }
                return of(response.shops);
            })
        )
    }

    requestPasswordReset(email: string) {
        return this.httpClient.post(`${this.url}/password_reset/request`, { email });
    }

    executePasswordReset(payload: any) {
        return this.httpClient.post(`${this.url}/password_reset/execute`, payload);
    }

    private fetchUserInfo(token: string): Promise<IUserInfo | undefined> {
        return lastValueFrom(
            token ?
                this.httpClient.get<IDataFetch>(
                    `${this.url}/user_info`, this.header)
                .pipe(
                    map((result: IDataFetch) => {
                        return result.content[0] as IUserInfo
                    })
                )
            : of (undefined)
        )
    }
}
